import React from "react";

const INLINE_LINK_CLASS =
  "font-medium text-theme-link-primary hover:text-theme-link-primary-hover hover:underline";

/** Matches http(s) URLs or email addresses embedded in FAQ copy. */
const INLINE_LINK_PATTERN =
  /(\bhttps?:\/\/[^\s]+|\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b)/g;

const URL_TRAILING_PUNCTUATION = /[.,;:!?)]+$/;

function splitHttpUrl(raw: string): { href: string; trailing: string } | null {
  if (!/^https?:\/\//i.test(raw)) {
    return null;
  }

  let href = raw;
  let trailing = "";
  while (URL_TRAILING_PUNCTUATION.test(href)) {
    const match = href.match(URL_TRAILING_PUNCTUATION);
    if (!match) {
      break;
    }
    trailing = match[0] + trailing;
    href = href.slice(0, -match[0].length);
  }

  return { href, trailing };
}

function isEmail(segment: string): boolean {
  return /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(segment);
}

/**
 * Turns plain-text http(s) URLs and email addresses into inline links.
 */
export function linkifyInlineText(text: string): React.ReactNode {
  const parts = text.split(INLINE_LINK_PATTERN);

  if (parts.length === 1) {
    return text;
  }

  return parts.map((part, index) => {
    if (!part) {
      return null;
    }

    const httpUrl = splitHttpUrl(part);
    if (httpUrl) {
      return (
        <React.Fragment key={`link-${index}`}>
          <a
            href={httpUrl.href}
            target="_blank"
            rel="noopener noreferrer"
            className={INLINE_LINK_CLASS}
          >
            {httpUrl.href}
          </a>
          {httpUrl.trailing}
        </React.Fragment>
      );
    }

    if (isEmail(part)) {
      return (
        <a
          key={`link-${index}`}
          href={`mailto:${part}`}
          className={INLINE_LINK_CLASS}
        >
          {part}
        </a>
      );
    }

    return <React.Fragment key={`text-${index}`}>{part}</React.Fragment>;
  });
}
